import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AttachmentIcon from "@mui/icons-material/Attachment";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { GeneralStyles } from "../../../assets/css/general.styles";
import { toast } from "react-toastify";
import Loader from "../../../components/loader";
import { OfferIssuerResource } from "../../../models/resource/offerIssuerResource";
import { OfferIssuerTouched } from "./offerIssuerTouched";
import {
  getOfferIssuerById,
  saveOfferIssuer,
} from "../../../reducers/offerIssuerReducer";
import { OfferIssuerModel } from "../../../models/models/offerIssuerModel";
import {
  SAVE_OFFERISSUER,
  VALIDATION_ACTIVATION_DATE_MESSAGE,
  VALIDATION_ATTACHED_FILES,
  VALIDATION_CMA_SUBMISSION_DATE_MESSAGE,
  VALIDATION_DETAILS_DATE_MESSAGE,
  VALIDATION_DETAILS_FINALIZING_DATE_MESSAGE,
  VALIDATION_ERROR_CIVIL_REQUIRED_MESSAGE,
  VALIDATION_ERROR_EARLIER_DATE_REQUIRED_MESSAGE,
  VALIDATION_ERROR_EARLIER_SPECIFIC_DATE_REQUIRED_MESSAGE,
  VALIDATION_ERROR_EMAIL_REQUIRED_MESSAGE,
  VALIDATION_ERROR_EXPIRED_DATE_REQUIRED_MESSAGE,
  VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE,
  VALIDATION_ERROR_INPUT_NUMBER_MESSAGE,
  VALIDATION_ERROR_PAID_INPUT_NUMBER_MESSAGE,
  VALIDATION_ERROR_PHONE_NUMBER_REQUIRED_MESSAGE,
  VALIDATION_ERROR_REQUIRED_MESSAGE,
  VALIDATION_ERROR_URL_REQUIRED_MESSAGE,
} from "../../../constants/actions";
import GridStyles from "../../../assets/css/grid.styles";
import CustomButton from "../../../components/customButton/customButton";
import CustomTabs from "../../../components/customTabs/customTabs";
import CustomTextField from "../../../components/customTextField/customTextField";
import CustomDatePicker from "../../../components/customDatePicker/customDatePicker";
import {
  isDateEarlierThanSpecificDate,
  isDateEarlierThanToday,
  isDateLaterThanToday,
  isDateTodayOrInFuture,
  parseDate,
} from "../../../helpers/useDateHelpers";
import CustomSelect from "../../../components/customSelect/customSelect";
import { getCountries } from "../../../reducers/countryReducer";
import { getCities } from "../../../reducers/cityReducer";
import { getAreas } from "../../../reducers/areaReducer";
import {
  getConcurrentlyIssuingOtherOffering,
  getExistingDividendOrDistributionPolicyList,
  getFundsPreviouslyOfferIssuer,
  getIncorporationJurisdictionList,
  getLegalProceedingsList,
  getLegalStructureofTheOfferIssuer,
  getOfferIssuerHaveRelationShip,
  getOfferIssuerPayList,
  getOfferIssuerRaiseFunds,
  getofferIssuerRaiseFundsOnAnotherPlatform,
  getPercentageFundsRaiseBefore,
  getPreventingDividendPolicyList,
  getPreventingPayingDividendList,
  getPreviouslyIssuingOtherOfferings,
} from "../../../reducers/lookupReducer";
import {
  AichasRelations,
  ConcurrentlyIssuingOtherOffering,
  Countries,
  FileTypes,
  JurisdictionOfIncorporationCountries,
  LegalProceedings,
  LegalStructureofTheOfferIssuer,
  LookupEnum,
  OfferIssuerValues,
  PreviouslyIssuingOtherOffering,
  RaiseFundsAnotherPlatform,
  Restrictions,
} from "../../../constants/enums";
import {
  civilIdCharactersMinimum,
  DigitsOnly,
  emailRegex,
  kuwaitPhoneNumberDigits,
  kuwaitPhoneNumberRegex,
  websiteRegex,
} from "../../../constants/registerConstants";
import { OfferIssuerDividenPolicyResource } from "../../../models/resource/offerIssuerDividenPolicyResource";
import { OfferIssuerDividenPolicyModel } from "../../../models/models/offerIssuerDividenPolicyModel";
import CustomTextArea from "../../../components/customTextArea/customTextArea";
import { validateCivilId } from "../../../helpers/useValidateCivilIdHelpers";
import CustomIcon from "../../../components/customIcon/customIcon";
import { FileResource } from "../../../models/resource/fileResource";
import { formatNumberWithCommas } from "../../../helpers/useAmountHelpers";

const OfferIssuerDetailsForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const offerIssuerDetails = useSelector(
    (state: RootState) => state.offerIssuer.offerIssuerDetails
  );
  const offerIssuers = useSelector(
    (state: RootState) => state.offerIssuer.offerIssuers
  );
  const countries = useSelector((state: RootState) => state.country.countries);
  const cities = useSelector((state: RootState) => state.city.cities);
  const areas = useSelector((state: RootState) => state.area.areas);
  const incorporationJurisdictionList = useSelector(
    (state: RootState) => state.lookup.incorporationJurisdictionList
  );
  const legalStructureofTheOfferIssuerList = useSelector(
    (state: RootState) => state.lookup.legalStructureofTheOfferIssuerList
  );
  const concurrentlyIssuingOtherOfferingList = useSelector(
    (state: RootState) => state.lookup.concurrentlyIssuingOtherOfferingList
  );
  const previouslyIssuingOtherOfferingsList = useSelector(
    (state: RootState) => state.lookup.previouslyIssuingOtherOfferingsList
  );
  const offerIssuerRaiseFundsList = useSelector(
    (state: RootState) => state.lookup.offerIssuerRaiseFundsList
  );
  const offerIssuerRaiseFundsAnotherPlatformList = useSelector(
    (state: RootState) => state.lookup.offerIssuerRaiseFundsAnotherPlatformList
  );
  const fundsPreviouslyOfferIssuerList = useSelector(
    (state: RootState) => state.lookup.fundsPreviouslyOfferIssuerList
  );
  const offerIssuerHaveRelationShipList = useSelector(
    (state: RootState) => state.lookup.offerIssuerHaveRelationShipList
  );
  const percentageFundsRaiseBeforeList = useSelector(
    (state: RootState) => state.lookup.percentageFundsRaiseBeforeList
  );

  const offerIssuerPayList = useSelector(
    (state: RootState) => state.lookup.offerIssuerPayList
  );
  const legalProceedingsList = useSelector(
    (state: RootState) => state.lookup.legalProceedingsList
  );
  const existingDividendOrDistributionPolicyList = useSelector(
    (state: RootState) => state.lookup.existingDividendOrDistributionPolicyList
  );
  const preventingDividendPolicyList = useSelector(
    (state: RootState) => state.lookup.preventingDividendPolicyList
  );
  const preventingPayingDividendList = useSelector(
    (state: RootState) => state.lookup.preventingPayingDividendList
  );
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [touched, setTouched] = useState<OfferIssuerTouched>({});
  const [selectedTab, setSelectedTab] = useState(1);
  const [dividenPolicy, setDividenPolicy] =
    useState<OfferIssuerDividenPolicyResource>({
      id: 0,
      doesPayId: null,
      existingPoliciesChangesId: null,
      preventingChangeExistId: null,
      payingDividendsRestrictionsExistId: null,
      legalProceedingsExistId: null,
      dividendPolicy: "",
      restrictions: "",
      legalProceedings: "",
    });
  const [formData, setFormData] = useState<OfferIssuerResource>({
    id: 0,
    name: "",
    fullCorporateName: "",
    companyCivilIdNumber: null,
    companyCivilIdExpiryDate: "",
    companyFloorNumber: 0,
    companyBuildingName: "",
    companyStreet: "",
    companyBlock: "",
    companyRegistrationNumber: "",
    companyCountryId: Countries.KuwaitId,
    companyCityId: null,
    companyAreaId: null,
    incorporationJurisdictionId: JurisdictionOfIncorporationCountries.KuwaitId,
    legalStructureId: null,
    otherLegalStructure: "",
    incorporationDate: "",
    telephoneNumber: null,
    associationArticlesDocument: null,
    mociRegistrationDocument: null,
    legalStructureDocument: null,
    email: "",
    website: "",
    isCompanyOtc: false,
    isAssociationArticlesDocumentProvided: false,
    isMociregistrationDocumentProvided: false,
    companyOtccode: "",
    registeredCrowdfundingPlatformName: "iMowazi",
    concurrentlyIssuingOtherOfferingsId: null,
    previouslyIssuingOtherOfferingsId: null,
    raiseFundsBeforeId: null,
    raiseFundsAnotherPlatformId: null,
    fundsPercentageId: null,
    fundsDispersedId: null,
    aichasRelationId: null,
    opportunityName: "",
    opportunityEndDate: "",
    raiseFundsAnotherPlatformEndDate: "",
    memorandum: "",
    executivesKycdetails: "",
    aicrelationship: "",
    previouslyIssuingOtherOffering: "",
    concurrentlyIssuingOtherOffering: "",
    directorsKycdetails: "",
    issuedCapital: 0,
    paidUpCapital: 0,
    fundsValue: 0,
    telephoneCountryId: Countries.KuwaitId,
    cumulativeValueDispersed12Months: 0,
    dividenPolicy,
    companyArea: "",
    companyCity: "",
    isAlmouwaziConfirmed: false,
    isAicConfirmed: false,
    isCmaConfirmed: false,
    aicConfirmationDate: "",
    almouwaziConfirmationDate: "",
    cmaSubmissionDate: "",
    almouwaziConfirmationComment: "",
    aicComment: "",
    cmaSubmissionComment: "",
    opportunities: [],
  });

  useEffect(() => {
    if (!authToken) {
      navigate("/Signin");
    }
  }, [authToken]);

  useEffect(() => {
    if (id) {
      const itemId = parseInt(id, 10);
      dispatch(getOfferIssuerById({ id: itemId }));
    }
  }, [id]);

  useEffect(() => {
    if (
      formData.companyCountryId &&
      formData.companyCountryId === Countries.KuwaitId
    ) {
      setFormData((prevData) => ({
        ...prevData,
        companyCity: "",
        companyArea: "",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        companyCityId: null,
        companyAreaId: null,
      }));
    }
  }, [formData.companyCountryId]);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(
      getIncorporationJurisdictionList({
        lookupId: LookupEnum.IncorporationJurisdiction,
      })
    );
    dispatch(
      getLegalStructureofTheOfferIssuer({
        lookupId: LookupEnum.LegalStructureofTheOfferIssuer,
      })
    );
    dispatch(
      getConcurrentlyIssuingOtherOffering({
        lookupId: LookupEnum.ConcurrentlyIssuingOtherOffering,
      })
    );
    dispatch(
      getPreviouslyIssuingOtherOfferings({
        lookupId: LookupEnum.PreviouslyIssuingOtherOfferings,
      })
    );
    dispatch(
      getOfferIssuerRaiseFunds({ lookupId: LookupEnum.OfferIssuerRaiseFunds })
    );
    dispatch(
      getofferIssuerRaiseFundsOnAnotherPlatform({
        lookupId: LookupEnum.OfferIssuerRaiseFundsAnotherPlatform,
      })
    );
    dispatch(
      getPercentageFundsRaiseBefore({
        lookupId: LookupEnum.PercentageFundsRaiseBefore,
      })
    );
    dispatch(
      getFundsPreviouslyOfferIssuer({
        lookupId: LookupEnum.FundsPreviouslyOfferIssuer,
      })
    );
    dispatch(
      getOfferIssuerHaveRelationShip({
        lookupId: LookupEnum.OfferIssuerHaveRelationShip,
      })
    );
    dispatch(getOfferIssuerPayList({ lookupId: LookupEnum.OfferIssuerPay }));
    dispatch(
      getExistingDividendOrDistributionPolicyList({
        lookupId: LookupEnum.ExistingDividendOrDistributionPolicyList,
      })
    );
    dispatch(
      getPreventingDividendPolicyList({
        lookupId: LookupEnum.PreventingDividendPolicy,
      })
    );
    dispatch(
      getPreventingPayingDividendList({
        lookupId: LookupEnum.PreventingPayingDividend,
      })
    );
    dispatch(
      getLegalProceedingsList({ lookupId: LookupEnum.LegalProceedings })
    );
  }, []);

  useEffect(() => {
    if (
      formData.companyCountryId &&
      formData.companyCountryId === Countries.KuwaitId
    ) {
      dispatch(getCities({ id: formData.companyCountryId }));
      dispatch(getAreas({ id: formData.companyCountryId }));
    }
  }, [formData.companyCountryId]);

  useEffect(() => {
    if (!formData.legalStructureId) {
      setFormData((prevData) => ({
        ...prevData,
        legalStructureDocument: null,
      }));
    }
  }, [formData.legalStructureId]);

  useEffect(() => {
    const itemData = offerIssuerDetails.data;
    if (itemData && id) {
      const data = itemData as OfferIssuerModel;
      const dividenPolicyData =
        data.dividenPolicy as OfferIssuerDividenPolicyModel;
      setDividenPolicy({
        id: dividenPolicyData.id || 0,
        doesPayId: dividenPolicyData.doesPayId || null,
        existingPoliciesChangesId:
          dividenPolicyData.existingPoliciesChangesId || null,
        legalProceedingsExistId:
          dividenPolicyData.legalProceedingsExistId || null,
        preventingChangeExistId:
          dividenPolicyData.preventingChangeExistId || null,
        payingDividendsRestrictionsExistId:
          dividenPolicyData.payingDividendsRestrictionsExistId || null,
        dividendPolicy: dividenPolicyData.dividendPolicy || "",
        restrictions: dividenPolicyData.restrictions || "",
        legalProceedings: dividenPolicyData.legalProceedings || "",
      });
      setFormData({
        id: data.id || 0,
        name: data.name || "",
        fullCorporateName: data.fullCorporateName || "",
        companyRegistrationNumber: data.companyRegistrationNumber || "",
        companyCivilIdNumber: data.companyCivilIdNumber || null,
        companyCivilIdExpiryDate: data.companyCivilIdExpiryDate || "",
        companyFloorNumber: data.companyFloorNumber || 0,
        companyBuildingName: data.companyBuildingName || "",
        companyStreet: data.companyStreet || "",
        companyBlock: data.companyBlock || "",
        companyCountryId: data.companyCountryId || null,
        companyCityId: data.companyCityId || null,
        companyAreaId: data.companyAreaId || null,
        legalStructureId: data.legalStructureId || null,
        otherLegalStructure: data.otherLegalStructure || "",
        associationArticlesDocument: data.associationArticlesDocument || null,
        mociRegistrationDocument: data.mociRegistrationDocument || null,
        legalStructureDocument: data.legalStructureDocument || null,
        concurrentlyIssuingOtherOfferingsId:
          data.concurrentlyIssuingOtherOfferingsId || null,
        previouslyIssuingOtherOfferingsId:
          data.previouslyIssuingOtherOfferingsId || null,
        raiseFundsBeforeId: data.raiseFundsBeforeId || null,
        raiseFundsAnotherPlatformId: data.raiseFundsAnotherPlatformId || null,
        fundsPercentageId: data.fundsPercentageId || null,
        fundsDispersedId: data.fundsDispersedId || null,
        aichasRelationId: data.aichasRelationId || null,
        telephoneCountryId: data.telephoneCountryId || null,
        incorporationJurisdictionId:
          data.incorporationJurisdictionId ||
          JurisdictionOfIncorporationCountries.KuwaitId,
        incorporationDate: data.incorporationDate || "",
        telephoneNumber: data.telephoneNumber || null,
        email: data.email || "",
        website: data.website || "",
        opportunityName: data.opportunityName || "",
        fundsValue: data.fundsValue || 0,
        isCompanyOtc: data.isCompanyOtc || false,
        isMociregistrationDocumentProvided:
          data.isMociregistrationDocumentProvided || false,
        isAssociationArticlesDocumentProvided:
          data.isAssociationArticlesDocumentProvided || false,
        companyOtccode: data.companyOtccode || "",
        registeredCrowdfundingPlatformName:
          data.registeredCrowdfundingPlatformName || "",
        opportunityEndDate: data.opportunityEndDate || "",
        raiseFundsAnotherPlatformEndDate:
          data.raiseFundsAnotherPlatformEndDate || "",
        memorandum: data.memorandum || "",
        directorsKycdetails: data.directorsKycdetails || "",
        executivesKycdetails: data.executivesKycdetails || "",
        aicrelationship: data.aicrelationship || "",
        previouslyIssuingOtherOffering:
          data.opportunities
            ?.map((opportunity) => opportunity.titleEn)
            .join("\n") || "",
        concurrentlyIssuingOtherOffering:
          data.concurrentlyIssuingOtherOffering || "",
        issuedCapital: data.issuedCapital || 0,
        paidUpCapital: data.paidUpCapital || 0,
        cumulativeValueDispersed12Months:
          data.cumulativeValueDispersed12Months || 0,
        dividenPolicy: data.dividenPolicy || dividenPolicy,
        companyArea: data.companyArea || "",
        companyCity: data.companyCity || "",
        isAlmouwaziConfirmed: data.isAlmouwaziConfirmed || false,
        isAicConfirmed: data.isAicConfirmed || false,
        isCmaConfirmed: data.isCmaConfirmed || false,
        almouwaziConfirmationDate: data.almouwaziConfirmationDate || "",
        almouwaziConfirmationComment: data.almouwaziConfirmationComment || "",
        aicComment: data.aicComment || "",
        cmaSubmissionComment: data.cmaSubmissionComment || "",
        aicConfirmationDate: data.aicConfirmationDate || "",
        cmaSubmissionDate: data.cmaSubmissionDate || "",
      });
    }
  }, [offerIssuerDetails.data]);

  const formatDate = (date: string): string => {
    if (!date) return "";
    const [day, month, year] = date.split("/");
    return `${day}/${month}/${year}`;
  };

    useEffect(() => {
      if (!formData.isAlmouwaziConfirmed) {
        setFormData({
          ...formData,
          almouwaziConfirmationDate: "",
          almouwaziConfirmationComment: "",
          aicComment: "",
          cmaSubmissionComment: "",
          aicConfirmationDate: "",
          cmaSubmissionDate: "",
        });
        setTouched((prevTouched) => ({
          ...prevTouched,
          almouwaziConfirmationDate: false,
          aicConfirmationDate: false,
          cmaSubmissionDate: false,
          startDate: false,
          endDate: false,
          collectFundsDeadline: false,
          activationDate: false,
        }));
      } else if (!formData.isAicConfirmed) {
        setFormData({
          ...formData,
          aicConfirmationDate: "",
          aicComment: "",
          cmaSubmissionComment: "",
          cmaSubmissionDate: "",
        });
        setTouched((prevTouched) => ({
          ...prevTouched,
          aicConfirmationDate: false,
          cmaSubmissionDate: false,
          startDate: false,
          endDate: false,
          collectFundsDeadline: false,
          activationDate: false,
        }));
      } else if (!formData.isCmaConfirmed) {
        setFormData({
          ...formData,
          cmaSubmissionDate: "",
          cmaSubmissionComment: "", 
        }); 
        setTouched((prevTouched) => ({
          ...prevTouched,
          cmaSubmissionDate: false,
          startDate: false,
          endDate: false,
          collectFundsDeadline: false, 
        }));
      }
    }, [
      formData.isAlmouwaziConfirmed,
      formData.isCmaConfirmed,
      formData.isAicConfirmed,
    ]);

    
      useEffect(() => {
        const validateCMARange = () => { 
          if (
            formData.almouwaziConfirmationDate &&
            formData.aicConfirmationDate
          ) {
            const almouwaziConfirmationDate = parseDate(
              formData.almouwaziConfirmationDate
            );
            const aicConfirmationDate = parseDate(
              formData.aicConfirmationDate
            );
            if (aicConfirmationDate && almouwaziConfirmationDate) {
              if (
                aicConfirmationDate < almouwaziConfirmationDate ||
                aicConfirmationDate > new Date()
              ) {
                setFormData((prevData) => ({
                  ...prevData,
                  aicConfirmationDate: "",
                }));
                setTouched((prevTouched) => ({
                  ...prevTouched,
                  aicConfirmationDate: true,
                }));
                toast.error(VALIDATION_DETAILS_FINALIZING_DATE_MESSAGE);
                return;
              }
            }
          }
          if (
            formData.cmaSubmissionDate &&
            formData.aicConfirmationDate
          ) {
            const cmaSubmissionDate = parseDate(formData.cmaSubmissionDate);
            const aicConfirmationDate = parseDate(
              formData.aicConfirmationDate
            );
            if (aicConfirmationDate && cmaSubmissionDate) {
              if (
                cmaSubmissionDate < aicConfirmationDate ||
                cmaSubmissionDate > new Date()
              ) {
                setFormData((prevData) => ({
                  ...prevData,
                  cmaSubmissionDate: "",
                }));
                setTouched((prevTouched) => ({
                  ...prevTouched,
                  cmaSubmissionDate: true,
                }));
                toast.error(VALIDATION_CMA_SUBMISSION_DATE_MESSAGE);
                return;
              }
            }
          } 
        };
        validateCMARange();
      }, [ 
        formData.almouwaziConfirmationDate,
        formData.aicConfirmationDate,
        formData.cmaSubmissionDate, 
      ]); 

  const handleSave = () => {
    if (
      formData.name.trim() === "" ||
      formData.fullCorporateName.trim() === "" ||
      formData.companyRegistrationNumber.trim() === "" ||
      formData.companyCivilIdExpiryDate.trim() === "" ||
      formData.companyBuildingName.trim() === "" ||
      formData.companyStreet.trim() === "" ||
      formData.companyBlock.trim() === "" ||
      formData.memorandum.trim() === "" ||
      formData.directorsKycdetails.trim() === "" ||
      formData.executivesKycdetails.trim() === "" ||
      (formData.aichasRelationId === AichasRelations.Yes &&
        formData.aicrelationship.trim() === "") ||
      (formData.concurrentlyIssuingOtherOfferingsId ===
        ConcurrentlyIssuingOtherOffering.Yes &&
        formData.concurrentlyIssuingOtherOffering.trim() === "") ||
      (formData.isCompanyOtc && formData.companyOtccode.trim() === "") ||
      formData.telephoneNumber === null ||
      (formData.telephoneCountryId === Countries.KuwaitId &&
        formData.telephoneNumber !== null &&
        (!kuwaitPhoneNumberRegex.test(formData.telephoneNumber.toString()) ||
          formData.telephoneNumber.toString().length !=
            kuwaitPhoneNumberDigits)) ||
      formData.telephoneNumber === null ||
      formData.telephoneCountryId === null ||
      (formData.companyCountryId === Countries.KuwaitId &&
        formData.companyCityId === null) ||
      (formData.companyCountryId === Countries.KuwaitId &&
        formData.companyAreaId === null) ||
      (formData.companyCountryId !== Countries.KuwaitId &&
        formData.companyCity.trim() === "") ||
      (formData.companyCountryId !== Countries.KuwaitId &&
        formData.companyArea.trim() === "") ||
      formData.incorporationJurisdictionId === null ||
      formData.legalStructureId === null ||
      (formData.legalStructureId === LegalStructureofTheOfferIssuer.Others &&
        formData.otherLegalStructure.trim() === "") ||
      formData.concurrentlyIssuingOtherOfferingsId === null ||
      (formData.id !=0 && formData.previouslyIssuingOtherOfferingsId === null) ||
      (formData.raiseFundsAnotherPlatformId === RaiseFundsAnotherPlatform.Yes &&
        formData.fundsPercentageId === null) ||
      (formData.raiseFundsAnotherPlatformId === RaiseFundsAnotherPlatform.Yes &&
        formData.opportunityName.trim() === "") ||
      (formData.raiseFundsAnotherPlatformId === RaiseFundsAnotherPlatform.Yes &&
        formData.raiseFundsAnotherPlatformEndDate.trim() === "") ||
      (formData.raiseFundsBeforeId === OfferIssuerValues.Yes &&
        formData.opportunityEndDate.trim() === "") ||
      formData.aichasRelationId === null ||
      formData.companyCountryId === null ||
      isNaN(Number(formData.companyFloorNumber)) ||
      (formData.issuedCapital !== null &&
        isNaN(Number(formData.issuedCapital))) ||
      formData.issuedCapital === 0 ||
      (formData.raiseFundsAnotherPlatformId === RaiseFundsAnotherPlatform.Yes &&
        formData.fundsValue !== null &&
        isNaN(Number(formData.fundsValue))) ||
      (formData.raiseFundsAnotherPlatformId === RaiseFundsAnotherPlatform.Yes &&
        formData.fundsValue === 0) ||
      (formData.paidUpCapital !== null &&
        (isNaN(Number(formData.paidUpCapital)) ||
          Number(formData.paidUpCapital) > Number(formData.issuedCapital))) ||
      formData.paidUpCapital === 0 ||
      (formData.id !== 0 && formData.cumulativeValueDispersed12Months !== 0 && isNaN(Number(formData.cumulativeValueDispersed12Months))) ||
      (formData.id !== 0 && formData.cumulativeValueDispersed12Months === 0) ||
      formData.email.trim() === "" ||
      !emailRegex.test(formData.email) ||
      formData.website.trim() === "" ||
      !websiteRegex.test(formData.website) ||
      (formData.companyCountryId === Countries.KuwaitId &&
        (formData.companyCivilIdNumber === null ||
          (formData.companyCivilIdNumber !== null &&
            !validateCivilId(formData.companyCivilIdNumber)))) ||
      dividenPolicy.doesPayId === null ||
      dividenPolicy.existingPoliciesChangesId === null ||
      dividenPolicy.payingDividendsRestrictionsExistId === null ||
      dividenPolicy.legalProceedingsExistId === null ||
      dividenPolicy.preventingChangeExistId === null ||
      (dividenPolicy.legalProceedingsExistId === LegalProceedings.Yes &&
        dividenPolicy.legalProceedings.trim() === "") ||
      (dividenPolicy.payingDividendsRestrictionsExistId === Restrictions.Yes &&
        dividenPolicy.restrictions.trim() === "") ||
      (formData.isAlmouwaziConfirmed &&
        formData.almouwaziConfirmationDate.trim() === "") ||
      (formData.isAicConfirmed &&
        formData.aicConfirmationDate.trim() === "") ||
      (formData.isCmaConfirmed &&
        formData.cmaSubmissionDate.trim() === "")
    ) {
      let touchedFields = {
        name: formData.name.trim() === "",
        email: formData.email.trim() === "" || !emailRegex.test(formData.email),
        website:
          formData.website.trim() === "" ||
          !websiteRegex.test(formData.website),
        fullCorporateName: formData.fullCorporateName.trim() === "",
        telephoneNumber:
          formData.telephoneNumber === null ||
          (formData.telephoneCountryId === Countries.KuwaitId &&
            formData.telephoneNumber !== null &&
            (!kuwaitPhoneNumberRegex.test(
              formData.telephoneNumber.toString()
            ) ||
              formData.telephoneNumber.toString().length !=
                kuwaitPhoneNumberDigits)) ||
          formData.telephoneNumber === null,
        companyRegistrationNumber:
          formData.companyRegistrationNumber.trim() === "",
        companyCivilIdExpiryDate: false,
        companyOtccode:
          formData.isCompanyOtc && formData.companyOtccode.trim() === "",
        incorporationDate: false,
        companyBuildingName: formData.companyBuildingName.trim() === "",
        companyStreet: formData.companyStreet.trim() === "",
        companyBlock: formData.companyBlock.trim() === "",
        memorandum: formData.memorandum.trim() === "",
        opportunityEndDate:
          formData.raiseFundsBeforeId === OfferIssuerValues.Yes &&
          formData.opportunityEndDate.trim() === "",
        raiseFundsAnotherPlatformEndDate:
          formData.raiseFundsAnotherPlatformId ===
            RaiseFundsAnotherPlatform.Yes &&
          formData.raiseFundsAnotherPlatformEndDate.trim() === "",
        directorsKycdetails: formData.directorsKycdetails.trim() === "",
        executivesKycdetails: formData.executivesKycdetails.trim() === "",
        companyCity:
          (formData.companyCountryId === Countries.KuwaitId &&
            formData.companyCityId === null) ||
          (formData.companyCountryId !== Countries.KuwaitId &&
            formData.companyCity.trim() === ""),
        telephoneCountryId: formData.telephoneCountryId === null,
        companyArea:
          (formData.companyCountryId === Countries.KuwaitId &&
            formData.companyAreaId === null) ||
          (formData.companyCountryId !== Countries.KuwaitId &&
            formData.companyArea.trim() === ""),
        incorporationJurisdictionId:
          formData.incorporationJurisdictionId === null,
        legalStructureId: formData.legalStructureId === null,
        otherLegalStructure:
          formData.legalStructureId === LegalStructureofTheOfferIssuer.Others &&
          formData.otherLegalStructure.trim() === "",
        previouslyIssuingOtherOfferingsId:formData.id !=0 && formData.previouslyIssuingOtherOfferingsId === null,
        concurrentlyIssuingOtherOfferingsId:
          formData.concurrentlyIssuingOtherOfferingsId === null,
        fundsPercentageId:
          formData.raiseFundsAnotherPlatformId ===
            RaiseFundsAnotherPlatform.Yes &&
          formData.fundsPercentageId === null,
        opportunityName:
          formData.raiseFundsAnotherPlatformId ===
            RaiseFundsAnotherPlatform.Yes &&
          formData.opportunityName.trim() === "",
        aichasRelationId: formData.aichasRelationId === null,
        companyCountryId: formData.companyCountryId === null,
        aicrelationship:
          formData.aichasRelationId === AichasRelations.Yes &&
          formData.aicrelationship.trim() === "",
        concurrentlyIssuingOtherOffering:
          formData.concurrentlyIssuingOtherOfferingsId ===
            ConcurrentlyIssuingOtherOffering.Yes &&
          formData.concurrentlyIssuingOtherOffering.trim() === "",
        companyCivilIdNumber:
          formData.companyCountryId === Countries.KuwaitId &&
          (formData.companyCivilIdNumber === null ||
            (formData.companyCivilIdNumber !== null &&
              !validateCivilId(formData.companyCivilIdNumber))),
        companyFloorNumber: isNaN(Number(formData.companyFloorNumber)),
        issuedCapital:
          (formData.issuedCapital !== null &&
            isNaN(Number(formData.issuedCapital))) ||
          formData.issuedCapital === 0,
        fundsValue:
          (formData.raiseFundsAnotherPlatformId ===
            RaiseFundsAnotherPlatform.Yes &&
            formData.fundsValue !== null &&
            isNaN(Number(formData.fundsValue))) ||
          (formData.raiseFundsAnotherPlatformId ===
            RaiseFundsAnotherPlatform.Yes &&
            formData.fundsValue === 0),
        paidUpCapital:
          (formData.paidUpCapital !== null &&
            (isNaN(Number(formData.paidUpCapital)) ||
              Number(formData.paidUpCapital) >
                Number(formData.issuedCapital))) ||
          formData.paidUpCapital === 0,
        cumulativeValueDispersed12Months: (formData.id !=0 && formData.cumulativeValueDispersed12Months !== null && isNaN(Number(formData.cumulativeValueDispersed12Months))) ||
          formData.cumulativeValueDispersed12Months === 0,
        doesPayId: dividenPolicy.doesPayId === null,
        existingPoliciesChangesId:
          dividenPolicy.existingPoliciesChangesId === null,
        legalProceedingsExistId: dividenPolicy.legalProceedingsExistId === null,
        payingDividendsRestrictionsExistId:
          dividenPolicy.payingDividendsRestrictionsExistId === null,
        preventingChangeExistId: dividenPolicy.preventingChangeExistId === null,
        legalProceedings:
          dividenPolicy.legalProceedingsExistId === LegalProceedings.Yes &&
          dividenPolicy.legalProceedings.trim() === "",
        restrictions:
          dividenPolicy.payingDividendsRestrictionsExistId ===
            Restrictions.Yes && dividenPolicy.restrictions.trim() === "",
        almouwaziConfirmationDate: formData.isAlmouwaziConfirmed && formData.almouwaziConfirmationDate.trim() === "",
        aicConfirmationDate: formData.isAicConfirmed &&  formData.aicConfirmationDate.trim() === "",
        cmaSubmissionDate: formData.isCmaConfirmed && formData.cmaSubmissionDate.trim() === "",
      };
      if (
        formData.companyCivilIdExpiryDate.trim() === "" ||
        (formData.companyCivilIdExpiryDate.trim() !== "" &&
          isDateEarlierThanToday(formData.companyCivilIdExpiryDate))
      ) {
        touchedFields.companyCivilIdExpiryDate = true;
      }

      if (
        formData.incorporationDate.trim() === "" ||
        (formData.incorporationDate.trim() !== "" &&
          !isDateEarlierThanToday(formData.incorporationDate)) ||
        isDateEarlierThanSpecificDate(formData.incorporationDate)
      ) {
        touchedFields.incorporationDate = true;
      }
      setTouched(touchedFields);
      toast.error(`${VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE}`);
      return;
    }

    if (
      formData.legalStructureId !== null &&
      formData.legalStructureDocument === null
    ) {
      setSelectedTab(1);
      toast.error(VALIDATION_ATTACHED_FILES);
      return;
    }
    if (
      (formData.isAssociationArticlesDocumentProvided &&
        formData.associationArticlesDocument === null) ||
      (formData.isMociregistrationDocumentProvided &&
        formData.associationArticlesDocument === null)
    ) {
      setSelectedTab(2);
      toast.error(VALIDATION_ATTACHED_FILES);
      return;
    }

    
    const formattedFormData = {
      ...formData,
      cmaSubmissionDate: formatDate(formData.cmaSubmissionDate),
      aicConfirmationDate: formatDate(formData.aicConfirmationDate ),
      dividenPolicy,
    };

    dispatch(saveOfferIssuer({ data: formattedFormData }))
      .unwrap()
      .then((response: any) => {
        const { success, message } = response;
        if (!success) {
          toast.error(message);
        } else {
          toast.success(`${SAVE_OFFERISSUER}`);
          navigate("/OfferIssuers");
        }
      })
      .catch((error) => {
        console.error("Failed to save item:", error);
      });
  };

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleCivilIdBlur = () => {
    if (
      formData.companyCountryId === Countries.KuwaitId &&
      (formData.companyCivilIdNumber === null ||
        (formData.companyCivilIdNumber !== null &&
          !validateCivilId(formData.companyCivilIdNumber)))
    ) {
      setTouched((prevData) => ({
        ...prevData,
        companyCivilIdNumber: true,
      }));
    } else {
      setTouched((prevData) => ({
        ...prevData,
        companyCivilIdNumber: false,
      }));
    }
  };

  const handleFileClick = (file: FileResource | null) => {
    if (file) {
      const binaryData = atob(file.data);
      const len = binaryData.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }
      const contentType = file.contentType || "application/octet-stream";
      const blob = new Blob([bytes], { type: contentType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleAttachFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = () => {
        const binaryData = reader.result as string;
        const base64String = window.btoa(binaryData);
        setFormData((prev) => ({
          ...prev,
          [name]: {
            typeId: getTypeId(name),
            fileName: file.name.substr(0, file.name.lastIndexOf(".")),
            extension: file.name.split(".").pop()?.toLowerCase() ?? "",
            contentType: file.type,
            data: base64String,
            date: "",
          },
        }));
      };

      reader.readAsBinaryString(file);
    }
  };

  const getTypeId = (name: string): number => {
    switch (name) {
      case "associationArticlesDocument":
        return FileTypes.AssociationArticlesDocuments;
      case "mociRegistrationDocument":
        return FileTypes.MociregistrationDocument;
      case "legalStructureDocument":
        return FileTypes.LegalStructureDocument;
      default:
        return 0;
    }
  };

  const tabs = [
    { id: 1, label: "Main info" },
    { id: 2, label: "Additional info" },
    { id: 3, label: "Dividend Policy" },
    ...(id ? [{ id: 4, label: "Confirmations" }] : []),
  ];

  const handleTelephoneNumberBlur = () => {
    if (
      (formData.telephoneCountryId === Countries.KuwaitId &&
        formData.telephoneNumber !== null &&
        (!kuwaitPhoneNumberRegex.test(formData.telephoneNumber.toString()) ||
          formData.telephoneNumber.toString().length !=
            kuwaitPhoneNumberDigits)) ||
      formData.telephoneNumber === null
    ) {
      setTouched((prevData) => ({
        ...prevData,
        telephoneNumber: true,
      }));
    } else {
      setTouched((prevData) => ({
        ...prevData,
        telephoneNumber: false,
      }));
    }
  };

  useEffect(() => {
    if (
      formData.telephoneCountryId &&
      formData.telephoneCountryId === Countries.KuwaitId &&
      formData.telephoneNumber !== null
    ) {
      handleTelephoneNumberBlur();
    }
  }, [formData.telephoneCountryId]);

  useEffect(() => {
    if (formData.raiseFundsBeforeId !== OfferIssuerValues.Yes) {
      setFormData({ ...formData, opportunityEndDate: "" });
    }
  }, [formData.raiseFundsBeforeId]);

  useEffect(() => {
    if (
      formData.raiseFundsAnotherPlatformId !== RaiseFundsAnotherPlatform.Yes
    ) {
      setTimeout(() => {
        setFormData((prev) => ({
          ...prev,
          raiseFundsAnotherPlatformEndDate: "",
        }));
      }, 0);
    }
  }, [formData.raiseFundsAnotherPlatformId]);

  useEffect(() => {
    if (
      formData.raiseFundsAnotherPlatformId !== RaiseFundsAnotherPlatform.Yes
    ) {
      setFormData({
        ...formData,
        opportunityName: "",
        fundsPercentageId: null,
        fundsValue: 0,
      });
    }
  }, [formData.raiseFundsAnotherPlatformId]);

  const handleEmailBlur = () => {
    if (!emailRegex.test(formData.email)) {
      setTouched((prevData) => ({
        ...prevData,
        email: true,
      }));
    }
  };

  const handleWebsiteBlur = () => {
    if (!websiteRegex.test(formData.website)) {
      setTouched((prevData) => ({
        ...prevData,
        website: true,
      }));
    }
  };

  return (
    <form style={GeneralStyles.formContainerStyle}>
      {offerIssuerDetails.isLoading && <Loader />}
      {offerIssuers.isLoading && <Loader />}
      <Grid
        container
        spacing={2}
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
      >
        <Grid item xs={12} sm={6} md={10}>
          {id ? (
            <h1 style={GridStyles.title}>
              Edit Offer Issuer <b>{formData.name}</b>
            </h1>
          ) : (
            <h1 style={GridStyles.title}>Add Offer Issuer</h1>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <div style={GridStyles.addButtonContainer}>
            <CustomButton
              type="button"
              style={GridStyles.addButton}
              onClick={handleSave}
            >
              <CheckCircleIcon style={GridStyles.iconStyle} /> Save
            </CustomButton>
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <CustomTabs
            selectedTab={selectedTab}
            handleTabChange={handleTabChange}
            tabs={tabs}
          />
          {selectedTab === 1 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Name of Offer Issuer"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        name: e.target.value,
                      }));
                    }}
                    required={true}
                    error={touched.name && formData.name.trim() === ""}
                    helperText={
                      touched.name && formData.name.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Full Corporate name of the Offer Issuer"
                    name="fullCorporateName"
                    type="text"
                    value={formData.fullCorporateName}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        fullCorporateName: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.fullCorporateName &&
                      formData.fullCorporateName.trim() === ""
                    }
                    helperText={
                      touched.fullCorporateName &&
                      formData.fullCorporateName.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Email Address"
                    name="email"
                    type="text"
                    value={formData.email}
                    onBlur={handleEmailBlur}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        email: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.email &&
                      (formData.email.trim() === "" ||
                        !emailRegex.test(formData.email))
                    }
                    helperText={
                      touched.email &&
                      (formData.email.trim() === "" ||
                        !emailRegex.test(formData.email))
                        ? `${VALIDATION_ERROR_EMAIL_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Website address of offer issuer"
                    name="website"
                    type="text"
                    value={formData.website}
                    onBlur={handleWebsiteBlur}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        website: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.website &&
                      (formData.website.trim() === "" ||
                        !websiteRegex.test(formData.website))
                    }
                    helperText={
                      touched.website &&
                      (formData.website.trim() === "" ||
                        !websiteRegex.test(formData.website))
                        ? `${VALIDATION_ERROR_URL_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomSelect
                    label="Company Country"
                    name="companyCountryId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(countries.data) ? (
                        countries.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.nameEn}
                          </MenuItem>
                        ))
                      ) : countries.data instanceof Object ? (
                        <MenuItem
                          key={(countries.data as any).id}
                          value={(countries.data as any).id}
                        >
                          {(countries.data as any).nameEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(countries.data)) {
                          const selected = countries.data.filter((item: any) =>
                            value.includes(item.id)
                          );
                          return selected.map((item) => item.nameEn).join(", ");
                        } else if (countries.data instanceof Object) {
                          return (countries.data as any).nameEn || "";
                        }
                        return "";
                      },
                    }}
                    value={formData.companyCountryId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        companyCountryId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.companyCountryId &&
                      formData.companyCountryId === null
                    }
                    helperText={
                      touched.companyCountryId &&
                      formData.companyCountryId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Civil id"
                    name="companyCivilIdNumber"
                    numericInput={true}
                    type="text"
                    value={formData.companyCivilIdNumber}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          companyCivilIdNumber: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          companyCivilIdNumber: true,
                        }));
                      }
                    }}
                    required={true}
                    onBlur={handleCivilIdBlur}
                    error={
                      touched.companyCivilIdNumber &&
                      formData.companyCountryId === Countries.KuwaitId &&
                      (formData.companyCivilIdNumber === null ||
                        (formData.companyCivilIdNumber !== null &&
                          !validateCivilId(formData.companyCivilIdNumber)))
                    }
                    helperText={
                      (touched.companyCivilIdNumber &&
                        (formData.companyCountryId === Countries.KuwaitId &&
                        (formData.companyCivilIdNumber === null ||
                          (formData.companyCivilIdNumber !== null &&
                            !validateCivilId(formData.companyCivilIdNumber)))
                          ? `${VALIDATION_ERROR_CIVIL_REQUIRED_MESSAGE}`
                          : "")) ||
                      ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    value={formData.companyCivilIdExpiryDate}
                    label="Expiry date of company civil id"
                    required={true}
                    onChange={(date: string) => {
                      setFormData({
                        ...formData,
                        companyCivilIdExpiryDate: date,
                      });
                      if (isDateEarlierThanToday(date)) {
                        setTouched((prevData) => ({
                          ...prevData,
                          companyCivilIdExpiryDate: true,
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          companyCivilIdExpiryDate: false,
                        }));
                      }
                    }}
                    error={touched.companyCivilIdExpiryDate}
                    helperText={
                      touched.companyCivilIdExpiryDate
                        ? `${VALIDATION_ERROR_EXPIRED_DATE_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Company registration number with the Ministry of Commerce and Industry"
                    name="companyRegistrationNumber"
                    type="text"
                    value={formData.companyRegistrationNumber}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        companyRegistrationNumber: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.companyRegistrationNumber &&
                      formData.companyRegistrationNumber.trim() === ""
                    }
                    helperText={
                      touched.companyRegistrationNumber &&
                      formData.companyRegistrationNumber.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                {formData.companyCountryId === Countries.KuwaitId ? (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <CustomSelect
                        label="Company City"
                        name="companyCityId"
                        type="text"
                        SelectProps={{
                          children: Array.isArray(cities.data) ? (
                            cities.data.map((item: any) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.nameEn}
                              </MenuItem>
                            ))
                          ) : cities.data instanceof Object ? (
                            <MenuItem
                              key={(cities.data as any).id}
                              value={(cities.data as any).id}
                            >
                              {(cities.data as any).nameEn}
                            </MenuItem>
                          ) : null,

                          IconComponent: () => null,
                          renderValue: (value: any) => {
                            if (Array.isArray(cities.data)) {
                              const selected = cities.data.filter((item: any) =>
                                value.includes(item.id)
                              );
                              return selected
                                .map((item) => item.nameEn)
                                .join(", ");
                            } else if (cities.data instanceof Object) {
                              return (cities.data as any).nameEn || "";
                            }
                            return "";
                          },
                        }}
                        value={formData.companyCityId || ""}
                        disabled={
                          formData.companyCountryId === null ? true : false
                        }
                        onChange={(e) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            companyCityId: e.target.value as number,
                          }))
                        }
                        multiple={false}
                        required={true}
                        error={
                          touched.companyCity && formData.companyCityId === null
                        }
                        helperText={
                          touched.companyCity && formData.companyCityId === null
                            ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CustomSelect
                        label="Company Area"
                        name="companyAreaId"
                        type="text"
                        SelectProps={{
                          children: Array.isArray(areas.data) ? (
                            areas.data.map((item: any) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.nameEn}
                              </MenuItem>
                            ))
                          ) : areas.data instanceof Object ? (
                            <MenuItem
                              key={(areas.data as any).id}
                              value={(areas.data as any).id}
                            >
                              {(areas.data as any).nameEn}
                            </MenuItem>
                          ) : null,

                          IconComponent: () => null,
                          renderValue: (value: any) => {
                            if (Array.isArray(areas.data)) {
                              const selected = areas.data.filter((item: any) =>
                                value.includes(item.id)
                              );
                              return selected
                                .map((item) => item.nameEn)
                                .join(", ");
                            } else if (areas.data instanceof Object) {
                              return (areas.data as any).nameEn || "";
                            }
                            return "";
                          },
                        }}
                        disabled={
                          formData.companyCountryId === null ? true : false
                        }
                        value={formData.companyAreaId || ""}
                        onChange={(e) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            companyAreaId: e.target.value as number,
                          }))
                        }
                        multiple={false}
                        required={true}
                        error={
                          touched.companyArea && formData.companyAreaId === null
                        }
                        helperText={
                          touched.companyArea && formData.companyAreaId === null
                            ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                            : ""
                        }
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <CustomTextField
                        label="Company City"
                        name="companyCity"
                        type="text"
                        value={formData.companyCity}
                        onChange={(e) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            companyCity: e.target.value,
                          }))
                        }
                        required={
                          formData.companyCountryId === Countries.KuwaitId
                            ? false
                            : true
                        }
                        error={
                          touched.companyCity &&
                          formData.companyCountryId !== Countries.KuwaitId &&
                          formData.companyCity.trim() === ""
                        }
                        helperText={
                          touched.companyCity &&
                          formData.companyCountryId !== Countries.KuwaitId &&
                          formData.companyCity.trim() === ""
                            ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CustomTextField
                        label="Company Area"
                        name="companyArea"
                        type="text"
                        value={formData.companyArea}
                        onChange={(e) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            companyArea: e.target.value,
                          }))
                        }
                        required={
                          formData.companyCountryId === Countries.KuwaitId
                            ? false
                            : true
                        }
                        error={
                          touched.companyArea &&
                          formData.companyCountryId !== Countries.KuwaitId &&
                          formData.companyArea.trim() === ""
                        }
                        helperText={
                          touched.companyArea &&
                          formData.companyCountryId !== Countries.KuwaitId &&
                          formData.companyArea.trim() === ""
                            ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                            : ""
                        }
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Company Street"
                    name="companyStreet"
                    type="text"
                    value={formData.companyStreet}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        companyStreet: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.companyStreet &&
                      formData.companyStreet.trim() === ""
                    }
                    helperText={
                      touched.companyStreet &&
                      formData.companyStreet.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Company Block"
                    name="companyBlock"
                    type="text"
                    value={formData.companyBlock}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        companyBlock: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.companyBlock &&
                      formData.companyBlock.trim() === ""
                    }
                    helperText={
                      touched.companyBlock &&
                      formData.companyBlock.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Company Building Name"
                    name="companyBuildingName"
                    type="text"
                    value={formData.companyBuildingName}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        companyBuildingName: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.companyBuildingName &&
                      formData.companyBuildingName.trim() === ""
                    }
                    helperText={
                      touched.companyBuildingName &&
                      formData.companyBuildingName.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Company Floor Number"
                    name="companyFloorNumber"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formData.companyFloorNumber}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          companyFloorNumber: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          companyFloorNumber: true,
                        }));
                      }
                    }}
                    required={true}
                    onBlur={() => {
                      if (isNaN(Number(formData.companyFloorNumber))) {
                        setTouched((prevData) => ({
                          ...prevData,
                          companyFloorNumber: true,
                        }));
                      }
                    }}
                    error={touched.companyFloorNumber}
                    helperText={
                      touched.companyFloorNumber
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid container item xs={12} sm={6} md={4} spacing={1}>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    container
                    style={{ display: "flex" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isCompanyOtc}
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setFormData({ ...formData, isCompanyOtc: checked });
                          }}
                          name="isCompanyOtc"
                        />
                      }
                      label="Registered on the OTC Platform?"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {formData.isCompanyOtc && (
                    <Grid item xs={12} sm={6} md={8}>
                      <CustomTextField
                        label="OTC Platform Code"
                        name="companyOtccode"
                        type="text"
                        value={formData.companyOtccode}
                        onChange={(e) => {
                          setFormData((prevData) => ({
                            ...prevData,
                            companyOtccode: e.target.value,
                          }));
                        }}
                        required={formData.isCompanyOtc}
                        error={
                          touched.companyOtccode &&
                          formData.isCompanyOtc &&
                          formData.companyOtccode.trim() === ""
                        }
                        helperText={
                          touched.companyOtccode &&
                          formData.isCompanyOtc &&
                          formData.companyOtccode.trim() === ""
                            ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                            : ""
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={formData.incorporationDate}
                    label="Date of incorporation"
                    required={true}
                    onChange={(date: string) => {
                      setFormData({ ...formData, incorporationDate: date });
                      if (
                        !isDateEarlierThanToday(date) ||
                        isDateEarlierThanSpecificDate(date)
                      ) {
                        setTouched((prevData) => ({
                          ...prevData,
                          incorporationDate: true,
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          incorporationDate: false,
                        }));
                      }
                    }}
                    error={touched.incorporationDate}
                    helperText={
                      touched.incorporationDate && formData.incorporationDate.trim() === ""
                        ? VALIDATION_ERROR_REQUIRED_MESSAGE
                        : touched.incorporationDate
                        ? `${VALIDATION_ERROR_EARLIER_SPECIFIC_DATE_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Jurisdiction of incorporation"
                    name="incorporationJurisdictionId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        incorporationJurisdictionList.data
                      ) ? (
                        incorporationJurisdictionList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : incorporationJurisdictionList.data instanceof
                        Object ? (
                        <MenuItem
                          key={(incorporationJurisdictionList.data as any).id}
                          value={(incorporationJurisdictionList.data as any).id}
                        >
                          {(incorporationJurisdictionList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(incorporationJurisdictionList.data)) {
                          const selected =
                            incorporationJurisdictionList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          incorporationJurisdictionList.data instanceof Object
                        ) {
                          return (
                            (incorporationJurisdictionList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.incorporationJurisdictionId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        incorporationJurisdictionId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.incorporationJurisdictionId &&
                      formData.incorporationJurisdictionId === null
                    }
                    helperText={
                      touched.incorporationJurisdictionId &&
                      formData.incorporationJurisdictionId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Telephone Number"
                    name="telephoneNumber"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formData.telephoneNumber}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          telephoneNumber: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          telephoneNumber: true,
                        }));
                      }
                    }}
                    required={true}
                    isPhoneNumber={true}
                    countryCode={formData.telephoneCountryId || 0}
                    countryCodes={
                      Array.isArray(countries.data) ? countries.data : []
                    }
                    onCountryCodeChange={(countryId) =>
                      setFormData({
                        ...formData,
                        telephoneCountryId: countryId,
                      })
                    }
                    onBlur={handleTelephoneNumberBlur}
                    error={
                      touched.telephoneNumber &&
                      (formData.telephoneNumber === null ||
                        (formData.telephoneCountryId === Countries.KuwaitId &&
                          formData.telephoneNumber !== null &&
                          (!kuwaitPhoneNumberRegex.test(
                            formData.telephoneNumber.toString()
                          ) ||
                            formData.telephoneNumber.toString().length !=
                              kuwaitPhoneNumberDigits)) ||
                        formData.telephoneNumber === null)
                    }
                    helperText={
                      (
                        touched.telephoneNumber &&
                        formData.telephoneNumber === null &&
                        formData.telephoneCountryId !== Countries.KuwaitId
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : touched.telephoneNumber &&
                            (formData.telephoneNumber === null ||
                              (formData.telephoneCountryId ===
                                Countries.KuwaitId &&
                                formData.telephoneNumber !== null &&
                                (!kuwaitPhoneNumberRegex.test(
                                  formData.telephoneNumber.toString()
                                ) ||
                                  formData.telephoneNumber.toString().length !=
                                    kuwaitPhoneNumberDigits)) ||
                              formData.telephoneNumber === null)
                      )
                        ? `${VALIDATION_ERROR_PHONE_NUMBER_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  container
                  alignItems="center"
                  spacing={2}
                >
                  <Grid item xs={12} sm={6} md={10}>
                    <CustomSelect
                      label="Legal Structure of the Offer Issuer"
                      name="legalStructureId"
                      type="text"
                      SelectProps={{
                        children: Array.isArray(
                          legalStructureofTheOfferIssuerList.data
                        ) ? (
                          legalStructureofTheOfferIssuerList.data.map(
                            (item: any) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.valueEn}
                              </MenuItem>
                            )
                          )
                        ) : legalStructureofTheOfferIssuerList.data instanceof
                          Object ? (
                          <MenuItem
                            key={
                              (legalStructureofTheOfferIssuerList.data as any)
                                .id
                            }
                            value={
                              (legalStructureofTheOfferIssuerList.data as any)
                                .id
                            }
                          >
                            {
                              (legalStructureofTheOfferIssuerList.data as any)
                                .valueEn
                            }
                          </MenuItem>
                        ) : null,
                        IconComponent: () => null,
                        renderValue: (value: any) => {
                          if (
                            Array.isArray(
                              legalStructureofTheOfferIssuerList.data
                            )
                          ) {
                            const selected =
                              legalStructureofTheOfferIssuerList.data.filter(
                                (item: any) => value.includes(item.id)
                              );
                            return selected
                              .map((item) => item.valueEn)
                              .join(", ");
                          } else if (
                            legalStructureofTheOfferIssuerList.data instanceof
                            Object
                          ) {
                            return (
                              (legalStructureofTheOfferIssuerList.data as any)
                                .valueEn || ""
                            );
                          }
                          return "";
                        },
                      }}
                      value={formData.legalStructureId || ""}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          legalStructureId: e.target.value as number,
                        }))
                      }
                      multiple={false}
                      required={true}
                      error={
                        touched.legalStructureId &&
                        formData.legalStructureId === null
                      }
                      helperText={
                        touched.legalStructureId &&
                        formData.legalStructureId === null
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      {formData.legalStructureDocument && (
                        <CustomIcon
                          tooltipText={
                            formData.legalStructureDocument.fileName || ""
                          }
                          onClick={() => {
                            handleFileClick(formData.legalStructureDocument);
                          }}
                          style={GridStyles.fileIcon}
                        >
                          <FileDownloadIcon />
                        </CustomIcon>
                      )}
                      <input
                        type="file"
                        name="legalStructureDocument"
                        ref={(el) =>
                          (fileInputRefs.current["legalStructureDocument"] = el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(e, "legalStructureDocument")
                        }
                      />
                      <CustomIcon
                        tooltipText="Attach"
                        onClick={() =>
                          fileInputRefs.current[
                            "legalStructureDocument"
                          ]?.click()
                        }
                        style={GridStyles.editIcon}
                        disabled={formData.legalStructureId === null}
                      >
                        <AttachmentIcon />
                      </CustomIcon>
                    </div>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <CustomTextField
                    label="Other Legal Structure"
                    name="otherLegalStructure"
                    type="text"
                    disabled={
                      formData.legalStructureId !==
                      LegalStructureofTheOfferIssuer.Others
                    }
                    value={formData.otherLegalStructure}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        otherLegalStructure: e.target.value,
                      }))
                    }
                    required={true}
                    error={
                      touched.otherLegalStructure &&
                      formData.otherLegalStructure.trim() === "" &&
                      formData.legalStructureId ===
                        LegalStructureofTheOfferIssuer.Others
                    }
                    helperText={
                      touched.otherLegalStructure &&
                      formData.otherLegalStructure.trim() === "" &&
                      formData.legalStructureId ===
                        LegalStructureofTheOfferIssuer.Others
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Does the offer issuer have a relationship with AIC?"
                    name="aichasRelationId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        offerIssuerHaveRelationShipList.data
                      ) ? (
                        offerIssuerHaveRelationShipList.data.map(
                          (item: any) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.valueEn}
                            </MenuItem>
                          )
                        )
                      ) : offerIssuerHaveRelationShipList.data instanceof
                        Object ? (
                        <MenuItem
                          key={(offerIssuerHaveRelationShipList.data as any).id}
                          value={
                            (offerIssuerHaveRelationShipList.data as any).id
                          }
                        >
                          {
                            (offerIssuerHaveRelationShipList.data as any)
                              .valueEn
                          }
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (
                          Array.isArray(offerIssuerHaveRelationShipList.data)
                        ) {
                          const selected =
                            offerIssuerHaveRelationShipList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          offerIssuerHaveRelationShipList.data instanceof Object
                        ) {
                          return (
                            (offerIssuerHaveRelationShipList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.aichasRelationId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        aichasRelationId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.aichasRelationId &&
                      formData.aichasRelationId === null
                    }
                    helperText={
                      touched.aichasRelationId &&
                      formData.aichasRelationId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  {formData.aichasRelationId === AichasRelations.Yes && (
                    <CustomTextArea
                      label="What is the relationship between AIC and the offer issuer?"
                      name="aicrelationship"
                      value={formData.aicrelationship}
                      onChange={(e) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          aicrelationship: e.target.value,
                        }));
                      }}
                      required={true}
                      error={
                        touched.aicrelationship &&
                        formData.aichasRelationId === AichasRelations.Yes &&
                        formData.aicrelationship.trim() === ""
                      }
                      helperText={
                        touched.aicrelationship &&
                        formData.aichasRelationId === AichasRelations.Yes &&
                        formData.aicrelationship.trim() === ""
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </>
          )}
          {selectedTab === 2 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Did the offer issuer provide the following documents:{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid container spacing={2}>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={
                      formData.isAssociationArticlesDocumentProvided ? 10 : 12
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            formData.isAssociationArticlesDocumentProvided
                          }
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setFormData({
                              ...formData,
                              isAssociationArticlesDocumentProvided: checked,
                            });
                          }}
                          name="isAssociationArticlesDocumentProvided"
                        />
                      }
                      label="Articles of Association"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {formData.isAssociationArticlesDocumentProvided && (
                    <>
                      {formData.associationArticlesDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              formData.associationArticlesDocument.fileName ||
                              ""
                            }
                            onClick={() => {
                              handleFileClick(
                                formData.associationArticlesDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        name="associationArticlesDocument"
                        ref={(el) =>
                          (fileInputRefs.current[
                            "associationArticlesDocument"
                          ] = el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(
                            e,
                            "associationArticlesDocument"
                          )
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "associationArticlesDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  spacing={1}
                  alignItems="center"
                >
                  <Grid
                    item
                    xs={12}
                    sm={formData.isMociregistrationDocumentProvided ? 10 : 12}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isMociregistrationDocumentProvided}
                          style={GeneralStyles.checkBoxStyle}
                          onChange={(e: any) => {
                            const { name, checked } = e.target;
                            setFormData({
                              ...formData,
                              isMociregistrationDocumentProvided: checked,
                            });
                          }}
                          name="isMociregistrationDocumentProvided"
                        />
                      }
                      label="Registration with MOCI"
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </Grid>
                  {formData.isMociregistrationDocumentProvided && (
                    <>
                      {formData.mociRegistrationDocument && (
                        <Grid item xs={12} sm={1}>
                          <CustomIcon
                            tooltipText={
                              formData.mociRegistrationDocument.fileName || ""
                            }
                            onClick={() => {
                              handleFileClick(
                                formData.mociRegistrationDocument
                              );
                            }}
                            style={GridStyles.fileIcon}
                          >
                            <FileDownloadIcon />
                          </CustomIcon>
                        </Grid>
                      )}
                      <input
                        type="file"
                        name="mociRegistrationDocument"
                        ref={(el) =>
                          (fileInputRefs.current["mociRegistrationDocument"] =
                            el)
                        }
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleAttachFileChange(e, "mociRegistrationDocument")
                        }
                      />
                      <Grid item xs={12} sm={1}>
                        <CustomIcon
                          tooltipText="Attach"
                          onClick={() =>
                            fileInputRefs.current[
                              "mociRegistrationDocument"
                            ]?.click()
                          }
                          style={GridStyles.editIcon}
                        >
                          <AttachmentIcon />
                        </CustomIcon>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Name of the Registered Crowdfunding Platform that the issuers want to list in"
                    name="registeredCrowdfundingPlatformName"
                    type="text"
                    value={formData.registeredCrowdfundingPlatformName}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        registeredCrowdfundingPlatformName: e.target.value,
                      }));
                    }}
                    required={false}
                    readOnly={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Issued capital"
                    name="issuedCapital"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formatNumberWithCommas(formData.issuedCapital)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          issuedCapital: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          issuedCapital: true,
                        }));
                      }
                    }}
                    required={true}
                    unit="KD"
                    error={
                      (touched.issuedCapital && formData.issuedCapital === 0) ||
                      (formData.issuedCapital !== null &&
                        isNaN(Number(formData.issuedCapital)))
                    }
                    helperText={
                      (touched.issuedCapital && formData.issuedCapital === 0) ||
                      (formData.issuedCapital !== null &&
                        isNaN(Number(formData.issuedCapital)))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                    label="Paid up capital"
                    name="paidUpCapital"
                    type="text"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formatNumberWithCommas(formData.paidUpCapital)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          paidUpCapital: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          paidUpCapital: true,
                        }));
                      }
                    }}
                    required={true}
                    unit="KD"
                    onBlur={() => {
                      if (
                        formData.paidUpCapital === 0 ||
                        (formData.paidUpCapital !== null &&
                          isNaN(Number(formData.paidUpCapital))) ||
                        Number(formData.paidUpCapital) >
                          Number(formData.issuedCapital)
                      ) {
                        setTouched((prevData) => ({
                          ...prevData,
                          paidUpCapital: true,
                        }));
                      }
                    }}
                    error={
                      touched.paidUpCapital &&
                      (formData.paidUpCapital === 0 ||
                        (formData.paidUpCapital !== null &&
                          isNaN(Number(formData.paidUpCapital))) ||
                        Number(formData.paidUpCapital) >
                          Number(formData.issuedCapital))
                    }
                    helperText={
                      touched.paidUpCapital &&
                      (formData.paidUpCapital === 0 ||
                        (formData.paidUpCapital !== null &&
                          isNaN(Number(formData.paidUpCapital))))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : touched.paidUpCapital &&
                          Number(formData.paidUpCapital) >
                            Number(formData.issuedCapital)
                        ? `${VALIDATION_ERROR_PAID_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Did the offer issuer raise funds through us before?"
                    name="raiseFundsBeforeId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        offerIssuerRaiseFundsList.data
                      ) ? (
                        offerIssuerRaiseFundsList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : offerIssuerRaiseFundsList.data instanceof Object ? (
                        <MenuItem
                          key={(offerIssuerRaiseFundsList.data as any).id}
                          value={(offerIssuerRaiseFundsList.data as any).id}
                        >
                          {(offerIssuerRaiseFundsList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(offerIssuerRaiseFundsList.data)) {
                          const selected =
                            offerIssuerRaiseFundsList.data.filter((item: any) =>
                              value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          offerIssuerRaiseFundsList.data instanceof Object
                        ) {
                          return (
                            (offerIssuerRaiseFundsList.data as any).valueEn ||
                            ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.raiseFundsBeforeId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        raiseFundsBeforeId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={false}
                    // error={touched.raiseFundsBeforeId && formData.raiseFundsBeforeId === null}
                    // helperText={touched.raiseFundsBeforeId && formData.raiseFundsBeforeId === null ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}` : ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={formData.opportunityEndDate}
                    label="Opportunity End Date"
                    required={
                      formData.raiseFundsBeforeId === OfferIssuerValues.Yes
                    }
                    disabled={
                      formData.raiseFundsBeforeId !== OfferIssuerValues.Yes
                    }
                    onChange={(date: string) => {
                      setFormData({ ...formData, opportunityEndDate: date });
                      if (isDateTodayOrInFuture(date)) {
                        setTouched((prevData) => ({
                          ...prevData,
                          opportunityEndDate: true,
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          opportunityEndDate: false,
                        }));
                      }
                    }}
                    error={
                      touched.opportunityEndDate &&
                      formData.raiseFundsBeforeId === OfferIssuerValues.Yes
                    }
                    helperText={
                      touched.opportunityEndDate &&
                      formData.raiseFundsBeforeId === OfferIssuerValues.Yes
                        ? `${VALIDATION_ERROR_EARLIER_DATE_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Was funds previously dispersed to the offer issuer?"
                    name="fundsDispersedId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        fundsPreviouslyOfferIssuerList.data
                      ) ? (
                        fundsPreviouslyOfferIssuerList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : fundsPreviouslyOfferIssuerList.data instanceof
                        Object ? (
                        <MenuItem
                          key={(fundsPreviouslyOfferIssuerList.data as any).id}
                          value={
                            (fundsPreviouslyOfferIssuerList.data as any).id
                          }
                        >
                          {(fundsPreviouslyOfferIssuerList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (
                          Array.isArray(fundsPreviouslyOfferIssuerList.data)
                        ) {
                          const selected =
                            fundsPreviouslyOfferIssuerList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          fundsPreviouslyOfferIssuerList.data instanceof Object
                        ) {
                          return (
                            (fundsPreviouslyOfferIssuerList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.fundsDispersedId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        fundsDispersedId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={false}
                    // error={touched.fundsDispersedId && formData.raiseFundsBeforeId === OfferIssuerValues.Yes && formData.fundsDispersedId === null}
                    // helperText={touched.fundsDispersedId && formData.raiseFundsBeforeId === OfferIssuerValues.Yes && formData.fundsDispersedId === null ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}` : ""}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomSelect
                    label="Has any funds been raised through another Platform?"
                    name="raiseFundsAnotherPlatformId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        offerIssuerRaiseFundsList.data
                      ) ? (
                        offerIssuerRaiseFundsList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : offerIssuerRaiseFundsList.data instanceof Object ? (
                        <MenuItem
                          key={(offerIssuerRaiseFundsList.data as any).id}
                          value={(offerIssuerRaiseFundsList.data as any).id}
                        >
                          {(offerIssuerRaiseFundsList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(offerIssuerRaiseFundsList.data)) {
                          const selected =
                            offerIssuerRaiseFundsList.data.filter((item: any) =>
                              value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          offerIssuerRaiseFundsList.data instanceof Object
                        ) {
                          return (
                            (offerIssuerRaiseFundsList.data as any).valueEn ||
                            ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.raiseFundsAnotherPlatformId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        raiseFundsAnotherPlatformId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={false}
                    // error={touched.raiseFundsAnotherPlatformId && formData.raiseFundsAnotherPlatformId === null}
                    // helperText={touched.raiseFundsAnotherPlatformId && formData.raiseFundsAnotherPlatformId === null ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}` : ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Name of the project or opportunity"
                    name="name"
                    type="text"
                    value={formData.opportunityName}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        opportunityName: e.target.value,
                      }));
                    }}
                    disabled={
                      formData.raiseFundsAnotherPlatformId !==
                      RaiseFundsAnotherPlatform.Yes
                    }
                    required={
                      formData.raiseFundsAnotherPlatformId ===
                      RaiseFundsAnotherPlatform.Yes
                    }
                    error={
                      touched.opportunityName &&
                      formData.raiseFundsAnotherPlatformId ===
                        RaiseFundsAnotherPlatform.Yes &&
                      formData.opportunityName.trim() === ""
                    }
                    helperText={
                      touched.opportunityName &&
                      formData.raiseFundsAnotherPlatformId ===
                        RaiseFundsAnotherPlatform.Yes &&
                      formData.opportunityName.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomSelect
                    label="% of funds raised before "
                    name="fundsPercentageId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        percentageFundsRaiseBeforeList.data
                      ) ? (
                        percentageFundsRaiseBeforeList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : percentageFundsRaiseBeforeList.data instanceof
                        Object ? (
                        <MenuItem
                          key={(percentageFundsRaiseBeforeList.data as any).id}
                          value={
                            (percentageFundsRaiseBeforeList.data as any).id
                          }
                        >
                          {(percentageFundsRaiseBeforeList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (
                          Array.isArray(percentageFundsRaiseBeforeList.data)
                        ) {
                          const selected =
                            percentageFundsRaiseBeforeList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          percentageFundsRaiseBeforeList.data instanceof Object
                        ) {
                          return (
                            (percentageFundsRaiseBeforeList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.fundsPercentageId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        fundsPercentageId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    disabled={
                      formData.raiseFundsAnotherPlatformId !==
                      RaiseFundsAnotherPlatform.Yes
                    }
                    required={
                      formData.raiseFundsAnotherPlatformId ===
                      RaiseFundsAnotherPlatform.Yes
                    }
                    error={
                      touched.fundsPercentageId &&
                      formData.raiseFundsAnotherPlatformId ===
                        RaiseFundsAnotherPlatform.Yes &&
                      formData.fundsPercentageId === null
                    }
                    helperText={
                      touched.fundsPercentageId &&
                      formData.raiseFundsAnotherPlatformId ===
                        RaiseFundsAnotherPlatform.Yes &&
                      formData.fundsPercentageId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Value of funds raised before"
                    name="name"
                    type="text"
                    unit="KD"
                    numericInput={true}
                    allowDecimal={true}
                    allowNegative={false}
                    value={formatNumberWithCommas(formData.fundsValue)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, "");
                      if (!isNaN(Number(value))) {
                        setFormData((prevData) => ({
                          ...prevData,
                          fundsValue: parseFloat(value),
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          fundsValue: true,
                        }));
                      }
                    }}
                    disabled={
                      formData.raiseFundsAnotherPlatformId !==
                      RaiseFundsAnotherPlatform.Yes
                    }
                    required={
                      formData.raiseFundsAnotherPlatformId ===
                      RaiseFundsAnotherPlatform.Yes
                    }
                    error={
                      (touched.fundsValue &&
                        formData.raiseFundsAnotherPlatformId ===
                          RaiseFundsAnotherPlatform.Yes &&
                        touched.fundsValue &&
                        formData.fundsValue === 0) ||
                      (formData.fundsValue !== null &&
                        isNaN(Number(formData.fundsValue)))
                    }
                    helperText={
                      touched.fundsValue &&
                      formData.raiseFundsAnotherPlatformId ===
                        RaiseFundsAnotherPlatform.Yes &&
                      ((touched.fundsValue && formData.fundsValue === 0) ||
                        (formData.fundsValue !== null &&
                          isNaN(Number(formData.fundsValue))))
                        ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    value={formData.raiseFundsAnotherPlatformEndDate || ""}
                    label="Raise Funds Another Platform End Date"
                    required={
                      formData.raiseFundsAnotherPlatformId ===
                      RaiseFundsAnotherPlatform.Yes
                    }
                    disabled={
                      formData.raiseFundsAnotherPlatformId !==
                      RaiseFundsAnotherPlatform.Yes
                    }
                    onChange={(date: string) => {
                      setFormData({
                        ...formData,
                        raiseFundsAnotherPlatformEndDate: date,
                      });
                      if (isDateTodayOrInFuture(date)) {
                        setTouched((prevData) => ({
                          ...prevData,
                          raiseFundsAnotherPlatformEndDate: true,
                        }));
                      } else {
                        setTouched((prevData) => ({
                          ...prevData,
                          raiseFundsAnotherPlatformEndDate: false,
                        }));
                      }
                    }}
                    error={
                      touched.raiseFundsAnotherPlatformEndDate &&
                      formData.raiseFundsAnotherPlatformId ===
                        RaiseFundsAnotherPlatform.Yes
                    }
                    helperText={
                      touched.raiseFundsAnotherPlatformEndDate &&
                      formData.raiseFundsAnotherPlatformId ===
                        RaiseFundsAnotherPlatform.Yes
                        ? `${VALIDATION_ERROR_EARLIER_DATE_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="Memorandum"
                    name="memorandum"
                    type="text"
                    value={formData.memorandum}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        memorandum: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.memorandum && formData.memorandum.trim() === ""
                    }
                    helperText={
                      touched.memorandum && formData.memorandum.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="KYC details for directors"
                    name="directorsKycdetails"
                    type="text"
                    value={formData.directorsKycdetails}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        directorsKycdetails: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.directorsKycdetails &&
                      formData.directorsKycdetails.trim() === ""
                    }
                    helperText={
                      touched.directorsKycdetails &&
                      formData.directorsKycdetails.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomTextField
                    label="KYC details for executives"
                    name="executivesKycdetails"
                    type="text"
                    value={formData.executivesKycdetails}
                    onChange={(e) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        executivesKycdetails: e.target.value,
                      }));
                    }}
                    required={true}
                    error={
                      touched.executivesKycdetails &&
                      formData.executivesKycdetails.trim() === ""
                    }
                    helperText={
                      touched.executivesKycdetails &&
                      formData.executivesKycdetails.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Is the Eligible Offer Issuer concurrently issuing any other offerings?"
                    name="concurrentlyIssuingOtherOfferingsId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        concurrentlyIssuingOtherOfferingList.data
                      ) ? (
                        concurrentlyIssuingOtherOfferingList.data.map(
                          (item: any) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.valueEn}
                            </MenuItem>
                          )
                        )
                      ) : concurrentlyIssuingOtherOfferingList.data instanceof
                        Object ? (
                        <MenuItem
                          key={
                            (concurrentlyIssuingOtherOfferingList.data as any)
                              .id
                          }
                          value={
                            (concurrentlyIssuingOtherOfferingList.data as any)
                              .id
                          }
                        >
                          {
                            (concurrentlyIssuingOtherOfferingList.data as any)
                              .valueEn
                          }
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (
                          Array.isArray(
                            concurrentlyIssuingOtherOfferingList.data
                          )
                        ) {
                          const selected =
                            concurrentlyIssuingOtherOfferingList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          concurrentlyIssuingOtherOfferingList.data instanceof
                          Object
                        ) {
                          return (
                            (concurrentlyIssuingOtherOfferingList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={formData.concurrentlyIssuingOtherOfferingsId || ""}
                    onChange={(e) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        concurrentlyIssuingOtherOfferingsId: e.target
                          .value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.concurrentlyIssuingOtherOfferingsId &&
                      formData.concurrentlyIssuingOtherOfferingsId === null
                    }
                    helperText={
                      touched.concurrentlyIssuingOtherOfferingsId &&
                      formData.concurrentlyIssuingOtherOfferingsId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid> 
                {id && ( 
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomSelect
                        label="Has the Eligible Offer Issuer previously issued any other offering?"
                        name="previouslyIssuingOtherOfferingsId"
                        type="text"
                        SelectProps={{
                          children: Array.isArray(
                            previouslyIssuingOtherOfferingsList.data
                          ) ? (
                            previouslyIssuingOtherOfferingsList.data.map(
                              (item: any) => (
                                <MenuItem key={item.id} value={item.id}>
                                  {item.valueEn}
                                </MenuItem>
                              )
                            )
                          ) : previouslyIssuingOtherOfferingsList.data instanceof
                            Object ? (
                            <MenuItem
                              key={
                                (previouslyIssuingOtherOfferingsList.data as any).id
                              }
                              value={
                                (previouslyIssuingOtherOfferingsList.data as any).id
                              }
                            >
                              {
                                (previouslyIssuingOtherOfferingsList.data as any)
                                  .valueEn
                              }
                            </MenuItem>
                          ) : null,

                          IconComponent: () => null,
                          renderValue: (value: any) => {
                            if (
                              Array.isArray(
                                previouslyIssuingOtherOfferingsList.data
                              )
                            ) {
                              const selected =
                                previouslyIssuingOtherOfferingsList.data.filter(
                                  (item: any) => value.includes(item.id)
                                );
                              return selected
                                .map((item) => item.valueEn)
                                .join(", ");
                            } else if (
                              previouslyIssuingOtherOfferingsList.data instanceof
                              Object
                            ) {
                              return (
                                (previouslyIssuingOtherOfferingsList.data as any)
                                  .valueEn || ""
                              );
                            }
                            return "";
                          },
                        }}
                        value={formData.previouslyIssuingOtherOfferingsId || ""}
                        onChange={(e) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            previouslyIssuingOtherOfferingsId: e.target
                              .value as number,
                          }))
                        }
                        multiple={false}
                        required={true}
                        readOnly={true}
                        error={
                          touched.previouslyIssuingOtherOfferingsId &&
                          formData.previouslyIssuingOtherOfferingsId === null
                        }
                        helperText={
                          touched.previouslyIssuingOtherOfferingsId &&
                          formData.previouslyIssuingOtherOfferingsId === null
                            ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                            : ""
                        }
                      />
                    </Grid> 
                    <Grid item xs={12} sm={6} md={4}>
                        <CustomTextField
                          label="Cumulative value of all amounts previously dispersed to the offer issuer in the last 12 months"
                          name="cumulativeValueDispersed12Months"
                          type="text"
                          numericInput={true}
                          allowDecimal={true}
                          allowNegative={false}
                          unit="KD"
                          value={formatNumberWithCommas(
                            formData.cumulativeValueDispersed12Months
                          )}
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, "");
                            if (!isNaN(Number(value))) {
                              setFormData((prevData) => ({
                                ...prevData,
                                cumulativeValueDispersed12Months: parseFloat(value),
                              }));
                            } else {
                              setTouched((prevData) => ({
                                ...prevData,
                                cumulativeValueDispersed12Months: true,
                              }));
                            }
                          }}
                          required={true}
                          error={
                            touched.cumulativeValueDispersed12Months &&
                            (formData.cumulativeValueDispersed12Months === 0 ||
                              (formData.cumulativeValueDispersed12Months !== null &&
                                isNaN(
                                  Number(formData.cumulativeValueDispersed12Months)
                                )))
                          }
                          helperText={
                            touched.cumulativeValueDispersed12Months &&
                            (formData.cumulativeValueDispersed12Months === 0 ||
                              (formData.cumulativeValueDispersed12Months !== null &&
                                isNaN(
                                  Number(formData.cumulativeValueDispersed12Months)
                                )))
                              ? `${VALIDATION_ERROR_INPUT_NUMBER_MESSAGE}`
                              : ""
                          }
                        />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  {formData.concurrentlyIssuingOtherOfferingsId ===
                    ConcurrentlyIssuingOtherOffering.Yes && (
                    <CustomTextArea
                      label="What is the other offering ?"
                      name="concurrentlyIssuingOtherOffering"
                      value={formData.concurrentlyIssuingOtherOffering}
                      onChange={(e) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          concurrentlyIssuingOtherOffering: e.target.value,
                        }));
                      }}
                      required={true}
                      error={
                        touched.concurrentlyIssuingOtherOffering &&
                        formData.concurrentlyIssuingOtherOfferingsId ===
                          ConcurrentlyIssuingOtherOffering.Yes &&
                        formData.concurrentlyIssuingOtherOffering.trim() === ""
                      }
                      helperText={
                        touched.concurrentlyIssuingOtherOffering &&
                        formData.concurrentlyIssuingOtherOfferingsId ===
                          ConcurrentlyIssuingOtherOffering.Yes &&
                        formData.concurrentlyIssuingOtherOffering.trim() === ""
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  {formData.previouslyIssuingOtherOfferingsId ===
                    PreviouslyIssuingOtherOffering.Yes && (
                    <CustomTextArea
                      label="What is the previously offering?"
                      name="previouslyIssuingOtherOffering"
                      value={formData.previouslyIssuingOtherOffering}
                      onChange={(e) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          previouslyIssuingOtherOffering: e.target.value,
                        }));
                      }}
                      readOnly={true}
                      required={false}
                      // error={(touched.previouslyIssuingOtherOffering && (formData.previouslyIssuingOtherOfferingsId ===  PreviouslyIssuingOtherOffering.Yes && formData.previouslyIssuingOtherOffering.trim() === "" ))}
                      // helperText={(touched.previouslyIssuingOtherOffering &&  (formData.previouslyIssuingOtherOfferingsId ===  PreviouslyIssuingOtherOffering.Yes && formData.previouslyIssuingOtherOffering.trim() === "" )) ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`: ''}
                    />
                  )}
                </Grid>
              </Grid>
            </>
          )}
          {selectedTab === 3 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Does the offer issuer pay"
                    name="doesPayId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(offerIssuerPayList.data) ? (
                        offerIssuerPayList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : offerIssuerPayList.data instanceof Object ? (
                        <MenuItem
                          key={(offerIssuerPayList.data as any).id}
                          value={(offerIssuerPayList.data as any).id}
                        >
                          {(offerIssuerPayList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(offerIssuerPayList.data)) {
                          const selected = offerIssuerPayList.data.filter(
                            (item: any) => value.includes(item.id)
                          );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (offerIssuerPayList.data instanceof Object) {
                          return (offerIssuerPayList.data as any).valueEn || "";
                        }
                        return "";
                      },
                    }}
                    value={dividenPolicy.doesPayId || ""}
                    onChange={(e) =>
                      setDividenPolicy((prevData) => ({
                        ...prevData,
                        doesPayId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.doesPayId && dividenPolicy.doesPayId === null
                    }
                    helperText={
                      touched.doesPayId && dividenPolicy.doesPayId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Are the any changes in the existing dividend or distribution policy?"
                    name="existingPoliciesChangesId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        existingDividendOrDistributionPolicyList.data
                      ) ? (
                        existingDividendOrDistributionPolicyList.data.map(
                          (item: any) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.valueEn}
                            </MenuItem>
                          )
                        )
                      ) : existingDividendOrDistributionPolicyList.data instanceof
                        Object ? (
                        <MenuItem
                          key={
                            (
                              existingDividendOrDistributionPolicyList.data as any
                            ).id
                          }
                          value={
                            (
                              existingDividendOrDistributionPolicyList.data as any
                            ).id
                          }
                        >
                          {
                            (
                              existingDividendOrDistributionPolicyList.data as any
                            ).valueEn
                          }
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (
                          Array.isArray(
                            existingDividendOrDistributionPolicyList.data
                          )
                        ) {
                          const selected =
                            existingDividendOrDistributionPolicyList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          existingDividendOrDistributionPolicyList.data instanceof
                          Object
                        ) {
                          return (
                            (
                              existingDividendOrDistributionPolicyList.data as any
                            ).valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={dividenPolicy.existingPoliciesChangesId || ""}
                    onChange={(e) =>
                      setDividenPolicy((prevData) => ({
                        ...prevData,
                        existingPoliciesChangesId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.existingPoliciesChangesId &&
                      dividenPolicy.existingPoliciesChangesId === null
                    }
                    helperText={
                      touched.existingPoliciesChangesId &&
                      dividenPolicy.existingPoliciesChangesId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    label="Will  there be any change preventing the offer issuer's dividend policy?"
                    name="preventingChangeExistId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        preventingDividendPolicyList.data
                      ) ? (
                        preventingDividendPolicyList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : preventingDividendPolicyList.data instanceof
                        Object ? (
                        <MenuItem
                          key={(preventingDividendPolicyList.data as any).id}
                          value={(preventingDividendPolicyList.data as any).id}
                        >
                          {(preventingDividendPolicyList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(preventingDividendPolicyList.data)) {
                          const selected =
                            preventingDividendPolicyList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          preventingDividendPolicyList.data instanceof Object
                        ) {
                          return (
                            (preventingDividendPolicyList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={dividenPolicy.preventingChangeExistId || ""}
                    onChange={(e) =>
                      setDividenPolicy((prevData) => ({
                        ...prevData,
                        preventingChangeExistId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.preventingChangeExistId &&
                      dividenPolicy.preventingChangeExistId === null
                    }
                    helperText={
                      touched.preventingChangeExistId &&
                      dividenPolicy.preventingChangeExistId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomSelect
                    label="Are there any restrictions preventing the offer issuer from paying dividends?"
                    name="payingDividendsRestrictionsExistId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(
                        preventingPayingDividendList.data
                      ) ? (
                        preventingPayingDividendList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : preventingPayingDividendList.data instanceof
                        Object ? (
                        <MenuItem
                          key={(preventingPayingDividendList.data as any).id}
                          value={(preventingPayingDividendList.data as any).id}
                        >
                          {(preventingPayingDividendList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(preventingPayingDividendList.data)) {
                          const selected =
                            preventingPayingDividendList.data.filter(
                              (item: any) => value.includes(item.id)
                            );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          preventingPayingDividendList.data instanceof Object
                        ) {
                          return (
                            (preventingPayingDividendList.data as any)
                              .valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={
                      dividenPolicy.payingDividendsRestrictionsExistId || ""
                    }
                    onChange={(e) =>
                      setDividenPolicy((prevData) => ({
                        ...prevData,
                        payingDividendsRestrictionsExistId: e.target
                          .value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.payingDividendsRestrictionsExistId &&
                      dividenPolicy.payingDividendsRestrictionsExistId === null
                    }
                    helperText={
                      touched.payingDividendsRestrictionsExistId &&
                      dividenPolicy.payingDividendsRestrictionsExistId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <CustomSelect
                    label="Are there any legal proceedings currently going on with the offer issuer or with any property or asset?"
                    name="legalProceedingsExistId"
                    type="text"
                    SelectProps={{
                      children: Array.isArray(legalProceedingsList.data) ? (
                        legalProceedingsList.data.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.valueEn}
                          </MenuItem>
                        ))
                      ) : legalProceedingsList.data instanceof Object ? (
                        <MenuItem
                          key={(legalProceedingsList.data as any).id}
                          value={(legalProceedingsList.data as any).id}
                        >
                          {(legalProceedingsList.data as any).valueEn}
                        </MenuItem>
                      ) : null,

                      IconComponent: () => null,
                      renderValue: (value: any) => {
                        if (Array.isArray(legalProceedingsList.data)) {
                          const selected = legalProceedingsList.data.filter(
                            (item: any) => value.includes(item.id)
                          );
                          return selected
                            .map((item) => item.valueEn)
                            .join(", ");
                        } else if (
                          legalProceedingsList.data instanceof Object
                        ) {
                          return (
                            (legalProceedingsList.data as any).valueEn || ""
                          );
                        }
                        return "";
                      },
                    }}
                    value={dividenPolicy.legalProceedingsExistId || ""}
                    onChange={(e) =>
                      setDividenPolicy((prevData) => ({
                        ...prevData,
                        legalProceedingsExistId: e.target.value as number,
                      }))
                    }
                    multiple={false}
                    required={true}
                    error={
                      touched.legalProceedingsExistId &&
                      dividenPolicy.legalProceedingsExistId === null
                    }
                    helperText={
                      touched.legalProceedingsExistId &&
                      dividenPolicy.legalProceedingsExistId === null
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={
                    dividenPolicy.payingDividendsRestrictionsExistId ===
                      Restrictions.Yes ||
                    dividenPolicy.legalProceedingsExistId ===
                      LegalProceedings.Yes
                      ? 4
                      : 12
                  }
                >
                  <CustomTextArea
                    label="Dividend Policy"
                    name="dividendPolicy"
                    value={dividenPolicy.dividendPolicy}
                    onChange={(e) =>
                      setDividenPolicy((prevData) => ({
                        ...prevData,
                        dividendPolicy: e.target.value,
                      }))
                    }
                    required={false}
                  />
                </Grid>
                {dividenPolicy.payingDividendsRestrictionsExistId ===
                  Restrictions.Yes && (
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomTextArea
                      label="Restrictions"
                      name="restrictions"
                      value={dividenPolicy.restrictions}
                      onChange={(e) =>
                        setDividenPolicy((prevData) => ({
                          ...prevData,
                          restrictions: e.target.value,
                        }))
                      }
                      required={true}
                      error={
                        touched.restrictions &&
                        dividenPolicy.payingDividendsRestrictionsExistId ===
                          Restrictions.Yes &&
                        dividenPolicy.restrictions.trim() === ""
                      }
                      helperText={
                        touched.restrictions &&
                        dividenPolicy.payingDividendsRestrictionsExistId ===
                          Restrictions.Yes &&
                        dividenPolicy.restrictions.trim() === ""
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                )}
                {dividenPolicy.legalProceedingsExistId ===
                  LegalProceedings.Yes && (
                  <Grid item xs={12} sm={6} md={4}>
                    <CustomTextArea
                      label="Legal Proceedings"
                      name="legalProceedings"
                      value={dividenPolicy.legalProceedings}
                      onChange={(e) =>
                        setDividenPolicy((prevData) => ({
                          ...prevData,
                          legalProceedings: e.target.value,
                        }))
                      }
                      required={true}
                      error={
                        touched.legalProceedings &&
                        dividenPolicy.legalProceedingsExistId ===
                          LegalProceedings.Yes &&
                        dividenPolicy.legalProceedings.trim() === ""
                      }
                      helperText={
                        touched.legalProceedings &&
                        dividenPolicy.legalProceedingsExistId ===
                          LegalProceedings.Yes &&
                        dividenPolicy.legalProceedings.trim() === ""
                          ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                          : ""
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}
           {selectedTab === 4 && (
            <>
              {/* <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Date of application for this opportunity{" "}
                {formData.applicationDate}
              </Typography> */}
              <Grid container spacing={2} mt={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isAlmouwaziConfirmed}
                        style={GeneralStyles.checkBoxStyle}
                        onChange={(e: any) => {
                          const { name, checked } = e.target;
                          setFormData({
                            ...formData,
                            isAlmouwaziConfirmed: checked,
                            isAicConfirmed: checked
                              ? formData.isAicConfirmed
                              : false,
                            isCmaConfirmed: checked
                              ? formData.isCmaConfirmed
                              : false,
                          });
                        }}
                        name="isAlmouwaziConfirmed"
                      />
                    }
                    label="Confirmed by Almowazi Administration"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={formData.almouwaziConfirmationDate}
                    label="Date of Almowazi Administration Confirmation"
                    required={true}
                    disabled={!formData.isAlmouwaziConfirmed}
                    onChange={(date: string) => {
                      setFormData({
                        ...formData,
                        almouwaziConfirmationDate: date,
                      });
                    }}
                    error={
                      touched.almouwaziConfirmationDate &&
                      formData.almouwaziConfirmationDate.trim() === ""
                    }
                    helperText={
                      touched.almouwaziConfirmationDate &&
                      formData.almouwaziConfirmationDate.trim() === ""
                        ? `${VALIDATION_DETAILS_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                      label="Comment"
                      name="almouwaziConfirmationComment"
                      type="text"
                      value={formData.almouwaziConfirmationComment}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          almouwaziConfirmationComment: e.target.value,
                        }))
                      }
                      required={false}
                      disabled={!formData.isAlmouwaziConfirmed} 
                    />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isAicConfirmed}
                        style={GeneralStyles.checkBoxStyle}
                        disabled={!formData.isAlmouwaziConfirmed}
                        onChange={(e: any) => {
                          const { name, checked } = e.target;
                          setFormData({
                            ...formData,
                            isAicConfirmed: checked,
                            isCmaConfirmed: checked
                              ? formData.isCmaConfirmed
                              : false,
                          });
                        }}
                        name="isAicConfirmed"
                      />
                    }
                    label="Confirmed by AIC"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={formData.aicConfirmationDate}
                    label="Date of AIC Confirmation"
                    required={true}
                    onChange={(date: string) => {
                      setFormData({
                        ...formData,
                        aicConfirmationDate: date,
                      });
                    }}
                    disabled={!formData.isAicConfirmed}
                    error={
                      touched.aicConfirmationDate &&
                      formData.aicConfirmationDate.trim() === ""
                    }
                    helperText={
                      touched.aicConfirmationDate &&
                      formData.aicConfirmationDate.trim() === ""
                        ? `${VALIDATION_DETAILS_FINALIZING_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                      label="Comment"
                      name="aicComment"
                      type="text"
                      value={formData.aicComment}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          aicComment: e.target.value,
                        }))
                      }
                      required={false}
                      disabled={!formData.isAicConfirmed}
                    />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isCmaConfirmed}
                        style={GeneralStyles.checkBoxStyle}
                        disabled={!formData.isAicConfirmed}
                        onChange={(e: any) => {
                          const { name, checked } = e.target;
                          setFormData({
                            ...formData,
                            isCmaConfirmed: checked,
                          });
                        }}
                        name="isCmaConfirmed"
                      />
                    }
                    label="Confirmed by CMA"
                    style={{
                      cursor: "pointer",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomDatePicker
                    value={formData.cmaSubmissionDate}
                    label="Date of CMA Confirmation"
                    required={true}
                    onChange={(date: string) => {
                      setFormData({
                        ...formData,
                        cmaSubmissionDate: date,
                      });
                    }}
                    disabled={!formData.isCmaConfirmed}
                    error={
                      touched.cmaSubmissionDate &&
                      formData.cmaSubmissionDate.trim() === ""
                    }
                    helperText={
                      touched.cmaSubmissionDate &&
                      formData.cmaSubmissionDate.trim() === ""
                        ? `${VALIDATION_CMA_SUBMISSION_DATE_MESSAGE}`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <CustomTextField
                      label="Comment"
                      name="cmaSubmissionComment"
                      type="text"
                      value={formData.cmaSubmissionComment}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          cmaSubmissionComment: e.target.value,
                        }))
                      }
                      required={false}
                      disabled={!formData.isCmaConfirmed}
                    />
                </Grid>
              </Grid> 
            </>
          )}
        </Grid>
      </Grid>
    </form>
  );
};

export default OfferIssuerDetailsForm;
